// JsonSchemaTab — content för "JSON Schema"-fliken i AiJsonDialog.
// Beskriver compact/lean-1-formaten, ikonpaletten, detalj-nivåerna
// och pre-emit self-check-reglerna. Extraherad ur AiJsonDialog (#4).

import {
  CARTOGRAPHER_PALETTE,
  DETAIL_LEVELS,
  SCHEMA_RULES,
} from '@/lib/cartographer/spec';

export function JsonSchemaTab() {
  return (
    <div className="mt-0 space-y-4 pr-4">
      <h3 className="text-lg font-semibold">Two formats, pick by Scan-läge</h3>
      <p className="text-muted-foreground">
        Flow Weaver accepts <strong>two</strong> import schemas. The Scan-läge selector in the
        upload-repo dialog chooses one; if you build by prompting an LLM, pick the same way:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        <div className="p-3 rounded-lg border-2 border-indigo-400/40 bg-indigo-50/30 dark:bg-indigo-950/20 space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-bold text-indigo-700 dark:text-indigo-300">COMPACT</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">single-sheet · system + grupper</span>
          </div>
          <p>Short keys (<code className="text-[11px]">b/cn/g</code>), lowercase-kebab icons (<code className="text-[11px]">git-branch</code>), abbreviated ports (<code className="text-[11px]">r/l/t/b</code>). Saves ~43% tokens. Connectors may reference layer ids.</p>
        </div>
        <div className="p-3 rounded-lg border-2 border-emerald-400/40 bg-emerald-50/30 dark:bg-emerald-950/20 space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-bold text-emerald-700 dark:text-emerald-300">LEAN-1</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">multi-sheet · färre grupper per sheet</span>
          </div>
          <p>Verbose keys (<code className="text-[11px]">sheets/cross</code>), PascalCase icons (<code className="text-[11px]">GitBranch</code>), full-word ports (<code className="text-[11px]">right/left</code>). Connectors must target <strong>blocks only</strong> — never group ids.</p>
        </div>
      </div>

      <div className="p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border-2 border-amber-400/40 text-xs space-y-1">
        <p className="font-semibold text-amber-700 dark:text-amber-300">Port-uniqueness rule (#1 import-rejection cause)</p>
        <p>Between any pair A↔B, each (block, port) anchors at most ONE connector. Reusing a port → import rejects the line.</p>
        <p className="font-mono text-[11px] mt-1">✓ A.t→B.t  +  A.tr→B.tl  +  A.br→B.bl</p>
        <p className="font-mono text-[11px]">✗ A.t→B.t  +  A.b →B.t   (B.t reused on target)</p>
      </div>

      <p className="text-muted-foreground">
        Both compact and full (verbose) keys are accepted on import. The deterministic
        upload-repo engine and the AI prompt below stay in sync with{' '}
        <code className="text-[11px]">src/lib/cartographer/spec.ts</code>.
      </p>

      <div>
        <h4 className="font-medium text-sm mb-2">Key Mapping (compact = full)</h4>
        <pre className="p-3 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words">
{`Root:        b=blocks  cn=connectors  g=groups  co=coordinateOrigin
Multi-sheet: sh=sheets  csc=crossSheetConnectors  asi=activeSheetIndex
Block:       t=type  cfg=config  clr=color
Config:      n=name  d=description (kort summering på block-kortet)
             dd=detailedDescription (markdown i Detaljer-dialogen)
             i=icon  s=subject
Connector:   f=fromId  to=toId  fp=fromPort  tp=toPort
             lb=label  bd=bidirectional  pl=pulse  plc=pulseColor
Cross-sheet: fsi=fromSheetIndex  tsi=toSheetIndex
             fbid=fromBlockId   tbid=toBlockId
Group:       bIds=blockIds  clr=color  n=name  lk=locked
             col=collapsed  cx=collapsedX  cy=collapsedY
             pg=parentGroupId

Port-uniqueness: each (block, port) on A↔B may anchor at most one
connector — reusing rejects on import.
Waypoints (w) are not rendered — do not emit them.`}
        </pre>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Description Fields (d vs dd)</h4>
        <div className="space-y-2 text-xs">
          <div className="p-3 rounded-lg border-2 border-blue-400/50 bg-blue-50/50 dark:bg-blue-950/20 space-y-1">
            <p className="font-bold text-blue-600 dark:text-blue-400 text-sm">"d" — short summary (always emit)</p>
            <p>One-paragraph plain text shown directly on the block-card in the canvas. Plain text only, ≤2000 chars. Use <code className="text-[11px]">\n</code> for line breaks.</p>
            <p className="font-mono text-[11px] bg-background/50 p-1.5 rounded">"d": "Validate credentials\\nCheck MFA status"</p>
          </div>
          <div className="p-3 rounded-lg border-2 border-amber-400/50 bg-amber-50/50 dark:bg-amber-950/20 space-y-1">
            <p className="font-bold text-amber-600 dark:text-amber-400 text-sm">"dd" — long markdown (opt-in per block)</p>
            <p>Rich markdown (headings, lists, code blocks) shown inside the Detaljer-dialog with attachments. ≤50000 chars. <strong>Emit ONLY</strong> when the block warrants real documentation — runbooks, schemas, decision rationale, acceptance criteria. Skip on trivial steps.</p>
            <p className="font-mono text-[11px] bg-background/50 p-1.5 rounded whitespace-pre-wrap">"dd": "## Auth runbook\n\n1. Verify password\n2. Check MFA\n\n```bash\ncurl -X POST ...\n```"</p>
            <p className="text-muted-foreground">Only included in "Copy JSON" / "Download JSON" when the user toggles <strong>Export all</strong> — keep dd substantial enough to be worth the opt-in.</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Block Types</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"ge"</strong> = general (any step)</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"co"</strong> = condition (decision)</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"cu"</strong> = create-user</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"dm"</strong> = disable-mailbox</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"d3"</strong> = delete-m365</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"se"</strong> = send-email</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"wh"</strong> = webhook</div>
          <div className="p-2 rounded bg-muted font-mono text-xs"><strong>"ag"</strong> = add-to-group</div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">8-Direction Port System</h4>
        <pre className="p-3 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap">
{`         tl ── t ── tr
         |           |
         l   BLOCK   r
         |           |
         bl ── b ── br

"t"=top  "tr"=top-right  "r"=right  "br"=bottom-right
"b"=bottom  "bl"=bottom-left  "l"=left  "tl"=top-left`}
        </pre>
        <p className="text-xs text-muted-foreground mt-2">
          Choose port based on relative block position: blocks to the right use fp="r", tp="l". Diagonal connections use corner ports.
        </p>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Coordinate System</h4>
        <div className="p-3 rounded-lg bg-muted text-xs space-y-1">
          <p><strong>Origin:</strong> Center of block (x,y is the midpoint, not top-left)</p>
          <p><strong>Grid snap:</strong> All coordinates must be multiples of 20 (auto-fixed on import if off)</p>
          <p><strong>Block size:</strong> 200×80 default (width × height)</p>
          <p><strong>Layer card:</strong> ~300×≥110 (height grows with chip-grid content)</p>
          <p><strong>Spacing:</strong> ~240px horizontal, ~220px vertical between blocks</p>
          <p><strong>First block:</strong> x:200, y:100 in each layer</p>
          <p><strong>Layer coords:</strong> Each collapsed layer has its own independent coordinate space</p>
          <p><strong>Import modes:</strong> Replace auto-centers; Append offsets diagonally below current bbox</p>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">The Three Entities</h4>
        <div className="space-y-3">
          <div className="p-3 rounded-lg border-2 border-indigo-400/50 bg-indigo-50/50 dark:bg-indigo-950/20 text-xs space-y-1">
            <p className="font-bold text-indigo-600 dark:text-indigo-400 text-sm">BLOCK — a single node</p>
            <p>A processing step, decision point, or action. Rendered as a 200×80 card with 8 ports.</p>
            <p className="font-mono text-[11px] bg-background/50 p-1.5 rounded">{'{"id":"b01","t":"ge","x":200,"y":100,"cfg":{"n":"Name"},"clr":"#6366f1"}'}</p>
            <p className="text-muted-foreground">Cannot contain other entities. Lives on a sheet or inside a group/layer.</p>
          </div>
          <div className="p-3 rounded-lg border-2 border-purple-400/50 bg-purple-50/50 dark:bg-purple-950/20 text-xs space-y-1">
            <p className="font-bold text-purple-600 dark:text-purple-400 text-sm">GROUP — visual frame (col:false)</p>
            <p>A dashed rectangle around blocks. Visual organization only — blocks remain visible and editable.</p>
            <p className="font-mono text-[11px] bg-background/50 p-1.5 rounded">{'{"id":"g1","bIds":["b01","b02"],"clr":"#8b5cf6"}'}</p>
            <p className="text-muted-foreground">Does NOT create navigation. No cx/cy needed. Blocks use parent canvas coordinates.</p>
          </div>
          <div className="p-3 rounded-lg border-2 border-emerald-400/50 bg-emerald-50/50 dark:bg-emerald-950/20 text-xs space-y-1">
            <p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">LAYER — navigable card (col:true)</p>
            <p>A 300×≥110 card on the parent canvas with a 2-column chip-grid showing direct children. Double-click to enter.</p>
            <p className="font-mono text-[11px] bg-background/50 p-1.5 rounded">{'{"id":"g1","bIds":["b01"],"clr":"#22c55e","n":"Settings","col":true,"cx":200,"cy":100}'}</p>
            <p className="text-muted-foreground">Creates a new depth level. REQUIRES cx/cy. Can be empty (bIds=[]) as a placeholder. Blocks inside use independent coordinates.</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Group / Layer Fields</h4>
        <div className="p-3 rounded-lg bg-muted text-xs space-y-1">
          <p><strong>col:true</strong> = Layer (compact card, navigable, needs cx/cy)</p>
          <p><strong>col:false / omitted</strong> = Group (visual frame around blocks)</p>
          <p><strong>pg:null</strong> = Top-level group/layer</p>
          <p><strong>pg:"g1"</strong> = Nested inside group/layer g1</p>
          <p><strong>cx/cy</strong> = Layer card position on parent canvas (multiples of 20)</p>
          <p><strong>bIds:[]</strong> = Empty layer (placeholder — navigate in to populate)</p>
          <p><strong>Nesting:</strong> No depth limit — layers inside layers inside layers</p>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Multi-Sheet (for 30+ blocks)</h4>
        <div className="p-3 rounded-lg bg-muted text-xs space-y-1">
          <p>Split large diagrams into multiple sheets — one per major subsystem. Sheets are tab-style siblings on the same workflow; cross-sheet connectors only allowed between adjacent sheets (|Δsheetindex| === 1).</p>
        </div>
        <pre className="mt-2 p-3 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words">
{`{
  "name": "...",
  "asi": 0,
  "co": "center",
  "sh": [
    { "id": "s0", "name": "① Frontend", "b": [...], "cn": [...], "g": [...] },
    { "id": "s1", "name": "② Backend",  "b": [...], "cn": [...], "g": [...] }
  ],
  "csc": [
    { "id": "x1", "fsi": 0, "tsi": 1,
      "fbid": "b-api", "tbid": "b-server",
      "fp": "r", "tp": "l", "lb": "HTTP" }
  ]
}

sh=sheets  csc=crossSheetConnectors  asi=activeSheetIndex
fsi=fromSheetIndex  tsi=toSheetIndex
fbid=fromBlockId    tbid=toBlockId`}
        </pre>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Port-Uniqueness Rule</h4>
        <div className="p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border-2 border-amber-400/40 text-xs space-y-1">
          <p>Between any blockpair (A↔B) each (block, port) may anchor at most ONE connector. Reusing a port → import REJECTS the line.</p>
          <p className="font-mono text-[11px] mt-2">✓ A.t→B.t  +  A.tr→B.tl  +  A.br→B.bl    (all ports unique)</p>
          <p className="font-mono text-[11px]">✗ A.t→B.t  +  A.b →B.t                   (B.t reused)</p>
          <p className="font-mono text-[11px]">✗ A.t→B.t  +  A.t →B.b                   (A.t reused)</p>
          <p className="text-muted-foreground mt-2">For dual-flow: use bd:true on a single connector (renders arrows at both ends), or two connectors with mirrored Y-axis ports (A.t→B.t paired with B.b→A.b).</p>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Layer Architecture Pattern</h4>
        <pre className="p-3 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap">
{`Root Canvas
├── Block "Start"
├── Block "Check Auth"
├── Layer "User Mgmt" (col:true, cx:400, cy:100)
│   ├── Block "Create User"     ← independent coords
│   ├── Block "Send Email"      ← inside the layer
│   └── Layer "MFA" (col:true, cx:200, cy:300, pg:"g1")
│       ├── Block "Validate"    ← nested layer coords
│       └── Block "Verify Code"
└── Group "Logging" (col:false)
    ├── Block "Audit Log"       ← visible on root canvas
    └── Block "Metrics"         ← same canvas as parent`}
        </pre>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Icons (Lucide)</h4>
        <pre className="p-3 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words">
{`People:    user, users
Comms:     mail, send, message-square, phone, bell
Cloud/Net: cloud, globe, server, database, link
Files:     file-text, folder, archive, download, upload, copy
Tools:     wrench, settings, terminal, filter, search
Status:    check-circle, alert-triangle, clock, flag, star
Security:  shield, lock, key, eye
Media:     play, camera, monitor
Logic:     git-branch, shuffle, layers, refresh-cw
Other:     zap, power, heart, tag, bookmark, bar-chart,
           package, trash, thumbs-up, calendar, help-circle`}
        </pre>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Domain Palette (locked — re-use everywhere)</h4>
        <p className="text-xs text-muted-foreground mb-2">
          Drives the upload-repo engine, the AI prompt below, and the in-app cartographer.
          Compact icons are lowercase-kebab; lean-1 uses the same name in PascalCase.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
          {CARTOGRAPHER_PALETTE.map((row) => (
            <div key={row.key} className="flex items-center gap-2 p-2 rounded bg-muted">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: row.color }} />
              <span className="flex-1">
                <strong>{row.label}</strong>{' '}
                <code className="text-[10px] text-muted-foreground">{row.color}</code>
              </span>
              <code className="text-[10px] text-muted-foreground">{row.iconKebab}</code>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Detail levels (controls blocks-per-group)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          {Object.values(DETAIL_LEVELS).map((d) => (
            <div key={d.key} className="p-2 rounded bg-muted space-y-0.5">
              <div className="font-semibold">{d.labelSv}</div>
              <div className="text-muted-foreground">
                ~{d.blocksPerGroupHint} blocks/group
              </div>
              <div className="text-[10px] text-muted-foreground">
                cap {d.maxBlocksPerGroup} · folder-collapse ≥ {d.folderCollapseThreshold}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Pre-emit self-check ({SCHEMA_RULES.length} rules)</h4>
        <p className="text-xs text-muted-foreground mb-2">
          Tick each before pasting into Import JSON. Rules tagged
          <span className="px-1 py-0.5 mx-1 rounded bg-indigo-100 dark:bg-indigo-950 text-[10px]">compact</span>
          or
          <span className="px-1 py-0.5 mx-1 rounded bg-emerald-100 dark:bg-emerald-950 text-[10px]">lean-1</span>
          only apply to that schema.
        </p>
        <ul className="space-y-1 text-xs">
          {SCHEMA_RULES.map((r) => (
            <li key={r.id} className="flex items-start gap-2 p-2 rounded bg-muted">
              <input type="checkbox" className="mt-0.5 flex-shrink-0" readOnly aria-label={r.label} />
              <div className="flex-1">
                <div className="font-medium flex items-center gap-1.5">
                  {r.label}
                  {r.scope !== 'both' && (
                    <span className={`px-1 py-0.5 rounded text-[10px] ${
                      r.scope === 'compact'
                        ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300'
                        : 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300'
                    }`}>
                      {r.scope}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{r.prose}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-2">Same example, side by side</h4>
        <p className="text-xs text-muted-foreground mb-2">
          A 3-block login flow rendered in both schemas. Pick by Scan-läge; the import
          parser accepts both.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-indigo-700 dark:text-indigo-300 font-semibold mb-1">Compact</div>
            <pre className="p-3 rounded-lg bg-muted font-mono text-[10px] whitespace-pre-wrap break-words border border-border">
{`{"name":"Login Flow","co":"center","b":[
  {"id":"b01","t":"ge","x":200,"y":100,
   "cfg":{"n":"Start","d":"Entry point","i":"play"},
   "clr":"#22c55e"},
  {"id":"b02","t":"co","x":440,"y":100,
   "cfg":{"n":"Auth Check","d":"Validate creds","i":"shield"},
   "clr":"#ef4444"},
  {"id":"b03","t":"ge","x":680,"y":100,
   "cfg":{"n":"Dashboard","d":"Main view","i":"monitor"},
   "clr":"#6366f1"}
],"cn":[
  {"id":"c01","f":"b01","to":"b02","fp":"r","tp":"l","lb":"submit"},
  {"id":"c02","f":"b02","to":"b03","fp":"r","tp":"l","lb":"success"}
],"g":[]}`}
            </pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1">Lean-1</div>
            <pre className="p-3 rounded-lg bg-muted font-mono text-[10px] whitespace-pre-wrap break-words border border-border">
{`{"format":"lean-1","name":"Login Flow","sheets":[
  {"id":"s0","name":"① Auth","color":"#1a2e5a",
   "blocks":[
    {"id":"s0-start","type":"general","x":200,"y":100,
     "name":"Start","description":"Entry point",
     "icon":"Play","color":"#22c55e"},
    {"id":"s0-auth","type":"condition","x":440,"y":100,
     "name":"Auth Check","description":"Validate creds",
     "icon":"Shield","color":"#ef4444"},
    {"id":"s0-dash","type":"general","x":680,"y":100,
     "name":"Dashboard","description":"Main view",
     "icon":"Monitor","color":"#6366f1"}],
   "connectors":[
    {"id":"s0-e1","from":"s0-start","to":"s0-auth",
     "fromPort":"right","toPort":"left","label":"submit"},
    {"id":"s0-e2","from":"s0-auth","to":"s0-dash",
     "fromPort":"right","toPort":"left","label":"success"}],
   "groups":[]}
],"cross":[]}`}
            </pre>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Note the casing: <code>t</code>/<code>i</code>/<code>fp</code> (compact) vs{' '}
          <code>type</code>/<code>icon</code>/<code>fromPort</code> (lean-1); and{' '}
          <code>play</code> vs <code>Play</code> for the icon name.
        </p>
      </div>
    </div>
  );
}
